import { FC } from "react";
import cn from "classnames";
import { Tab, TabGroup, TabList } from "@headlessui/react";
import { CaretRightLine } from "@/icons";
import { useTabContext } from "@/components/TabPanels/TabContext";
import { getDataAutomationGroup, getDataLinkAttributes } from "@/helpers";

type EvolutionTabNavigationProps = {
  tabClickOverride?: boolean;
};

export const EvolutionTabNavigation: FC<EvolutionTabNavigationProps> = ({
  tabClickOverride = false,
}) => {
  const { tabs, activeTab, setActiveTab } = useTabContext();

  if (!tabs?.length) return null;

  return (
    <TabGroup
      manual
      selectedIndex={activeTab}
      onChange={setActiveTab}
      as="span"
      className="block"
    >
      <TabList
        as="ul"
        className="flex w-full max-w-308 flex-col items-center justify-between gap-x-5 sm:grid sm:grid-cols-[repeat(auto-fit,minmax(13rem,1fr))] sm:gap-y-4"
      >
        {tabs?.map((tab, index) => (
          <Tab
            {...getDataLinkAttributes({ dld: tab.dld, dlt: tab.dlt })}
            {...getDataAutomationGroup("tab-card")}
            data-automation-id={`tab-${index}`}
            key={`${tab.label}-${index}`}
            className={cn(
              "group first:border-t-swdc-warm-gray-175 relative flex w-full flex-row justify-between gap-x-5 pt-3 whitespace-nowrap first:border-t focus-visible:outline-none sm:max-w-103 sm:pt-0 sm:first:border-t-0",
              index === activeTab
                ? "bg-swdc-warm-gray-50 sm:bg-transparent"
                : "border-b-swdc-warm-gray-175 border-b sm:border-b-0",
            )}
            as="li"
          >
            <div
              onClick={() => tabClickOverride && setActiveTab(index)}
              className={cn(
                "font-space-mono text-body-s relative w-full flex-1 cursor-pointer tracking-[0.1875rem] sm:max-w-92 sm:text-center",
                "group-data-focus:before:border-swdc-blue-325 group-data-focus:before:absolute group-data-focus:before:-top-px group-data-focus:before:-left-px group-data-focus:before:z-1 group-data-focus:before:box-border group-data-focus:before:h-[calc(100%+0.375rem)] group-data-focus:before:w-[calc(100%+2px)] group-data-focus:before:rounded-md group-data-focus:before:border-4 group-data-focus:before:border-solid group-data-focus:before:content-['']",
                "group-data-focus:after:absolute group-data-focus:after:-top-px group-data-focus:after:-left-px group-data-focus:after:z-1 group-data-focus:after:box-border group-data-focus:after:h-[calc(100%+0.375rem)] group-data-focus:after:w-[calc(100%+2px)] group-data-focus:after:rounded-md group-data-focus:after:border-2 group-data-focus:after:border-solid group-data-focus:after:border-white group-data-focus:after:content-['']",
                index === activeTab
                  ? "text-swdc-black-600"
                  : "text-swdc-warm-gray-450 hover:text-swdc-black-600 active:text-swdc-blue-400-new",
              )}
            >
              {tab.label}
              <div
                className={cn(
                  "bg-swdc-orange-400 relative z-0 mt-3 h-0.5 origin-left rounded-full transition-transform duration-300 ease-out sm:mt-1.5 sm:origin-center",
                  index === activeTab ? "scale-x-100" : "scale-x-0",
                )}
              ></div>
            </div>
            <CaretRightLine
              width={24}
              height={24}
              fill="var(--color-swdc-warm-gray-175)"
              className={cn(
                "hidden sm:block",
                index === tabs.length - 1 && "sm:invisible",
              )}
            />
          </Tab>
        ))}
      </TabList>
    </TabGroup>
  );
};
